import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./LatentPaywall.module.scss"

/**
 * LatentPaywall (`/latent`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: FEATURE-SPECIFIC.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — one real security
 * feature, told through the single moment where privacy is won or lost.
 *
 * ── The feature ─────────────────────────────────────────────────────────────
 * PRIVATE CAMERA — a genuine Private Photo Vault Premium feature (localized
 * `features.camera`: "Private Camera"). Shoot photos and video from inside the
 * app and every frame is written straight into the encrypted vault. It never
 * lands on your Camera Roll, never shows up in Recents, a shared album, the
 * photo widget, or an automatic cloud sync — because it was never there to
 * begin with.
 *
 * ── Concept ─────────────────────────────────────────────────────────────────
 * "Shot straight into the vault." Every ordinary camera has one default that
 * quietly costs you: the instant you tap the shutter, the photo is written to
 * the Camera Roll — the open shelf anyone who picks up your phone can scroll to.
 * A "latent image" (the photographic term) is a frame that has been captured but
 * not yet made visible to anyone. Private Camera keeps every capture latent to
 * the world and visible only to you. This paywall SELLS THAT ONE FEATURE — the
 * star of the screen is the routing of a newborn photo, not a bullet list.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * PRESS-THE-SHUTTER + THROW-THE-ROUTING-SWITCH. The hero is a live camera
 * console. Press the shutter and a frame is born; a rail-points SWITCH under the
 * viewfinder routes that newborn frame down one of two diverging lanes into one
 * of two bins: the open "Camera Roll · anyone" (amber, exposed) or "Your Vault ·
 * only you" (blue, sealed). Private Camera keeps the switch thrown to the Vault,
 * so the Camera Roll counter stays frozen at 0 — "never written to the roll" —
 * while the vault fills and seals. You can THROW the switch to the roll to feel,
 * honestly, exactly what an ordinary camera does with the same shot, then throw
 * it back. A press-and-route / throw-the-points mechanic — not a tilt (Facedown),
 * a ring-sweep (Aperture), a tap-keypad (Only You), a trace-a-pattern (Trace/
 * Glyph), a pour (Cascade), a draw-a-link (Overair) or a tap-to-save-down-a-chute
 * (Slipstream). The photo is captured at the shutter, not imported from elsewhere.
 *
 * ── Divergence (vs. the 80+ prior concepts) ────────────────────────────────
 * Layout      : a dark CAMERA CONSOLE — live viewfinder over a two-lane routing
 *               rig with two destination bins — not a wall, door, grid, keypad,
 *               lens barrel, browser, pour-vessels or before/after seam.
 * Interaction : PRESS-SHUTTER + THROW-THE-POINTS — capture a frame and reroute
 *               its fate between two bins. New to the set.
 * Metaphor    : the RAIL SWITCH at the moment of capture — where the newborn
 *               photo is sent decides who can ever see it.
 * Value frame : CONTRAST at the source — "never written to the roll." Not loss,
 *               fear, scarcity or nostalgia; the honest gap between where an
 *               ordinary camera puts a photo and where Private Camera puts it.
 * Palette     : dark camera console; brand blue = the sealed vault, warm amber =
 *               the exposed roll. Precise, quiet, confident.
 * Register    : control and relief — privacy decided before the photo exists to
 *               anyone else.
 * CTA         : "Shoot into the vault" — yearly featured.
 *
 * ── Honest & App Store compliant ────────────────────────────────────────────
 * Private Camera is a REAL feature. The supporting trust chips are real too
 * (only-you lock with Face ID / Touch ID / a code · encrypted Cloud Vault ·
 * Break-In Reports). NOTHING is ever deleted and nothing leaves the phone: the
 * Camera Roll "0" describes where NEW in-app captures are written, not a threat
 * to anything you already have — there is NO data-loss claim, NO countdown, NO
 * fabricated scarcity. The "Camera Roll" lane is clearly labelled as what an
 * ordinary camera does, shown for honest contrast, not as a claim about this
 * product. "Save 79%" is exact arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small and secondary. The
 * 7-day free trial is the real trial. Renders on built-in debug product data
 * with bundled photos — no native injection, no external egress.
 */

// The frames the camera captures — bundled, self-contained repo photos.
const MEMORIES = [
  "/byheart/m04.jpg",
  "/byheart/m11.jpg",
  "/byheart/m02.jpg",
  "/byheart/m15.jpg",
  "/byheart/m07.jpg",
  "/byheart/m13.jpg",
  "/byheart/m09.jpg",
  "/byheart/m17.jpg",
]

// Real supporting Private Photo Vault safeguards — a compact trust row, NOT the
// star of the screen. Private Camera is the feature being sold.
const TRUST = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID · Touch ID · code",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private, encrypted backup",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Reports",
    line: "See any wrong attempt",
  },
]

type Route = "vault" | "roll"
interface Fly {
  id: number
  src: string
  route: Route
}

const MAX_STACK = 5
const FLY_MS = 620

const CameraGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.2l.9-1.4A1.5 1.5 0 0 1 9.9 4h4.2a1.5 1.5 0 0 1 1.3.6L16.3 6h1.2A2.5 2.5 0 0 1 20 8.5v8A2.5 2.5 0 0 1 17.5 19h-11A2.5 2.5 0 0 1 4 16.5v-8Z"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <circle cx="12" cy="12.2" r="3.2" stroke="currentColor" strokeWidth="1.7" />
  </svg>
)

const LockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="5"
      y="10.5"
      width="14"
      height="9.5"
      rx="2.4"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
)

const EyeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="2.7" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

const CheckGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 6.5 10 17l-5-4.6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LatentPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (yearly featured, monthly secondary) ──────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savePercent = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── camera state ──────────────────────────────────────────────────────────
  const [route, setRoute] = useState<Route>("vault")
  const [vaultShots, setVaultShots] = useState<string[]>([])
  const [rollShots, setRollShots] = useState<string[]>([])
  const [flying, setFlying] = useState<Fly[]>([])
  const [flash, setFlash] = useState(false)
  const [pop, setPop] = useState(false)

  const rigRef = useRef<HTMLDivElement | null>(null)
  const forkRef = useRef<HTMLDivElement | null>(null)
  const vaultBinRef = useRef<HTMLDivElement | null>(null)
  const rollBinRef = useRef<HTMLDivElement | null>(null)

  const shotCounter = useRef(0)
  const frameIdx = useRef(0)
  const routeRef = useRef<Route>("vault")
  const flyStarted = useRef<Set<number>>(new Set())
  const timers = useRef<number[]>([])

  useEffect(() => {
    routeRef.current = route
  }, [route])

  const pushTimer = (fn: () => void, ms: number) => {
    const t = window.setTimeout(fn, ms)
    timers.current.push(t)
    return t
  }

  const currentFrame = MEMORIES[frameIdx.current % MEMORIES.length]

  const land = useCallback((item: Fly) => {
    setFlying((f) => f.filter((x) => x.id !== item.id))
    if (item.route === "vault") {
      setVaultShots((s) => [...s, item.src])
    } else {
      setRollShots((s) => [...s, item.src])
    }
  }, [])

  // capture a frame and route it to the currently-armed bin
  const capture = useCallback(
    (forceRoute?: Route) => {
      if (flying.length >= 4) return // guard against runaway taps
      const r = forceRoute ?? routeRef.current
      const src = MEMORIES[frameIdx.current % MEMORIES.length]
      frameIdx.current += 1
      const id = ++shotCounter.current

      if (!reduceMotion) {
        setFlash(true)
        setPop(true)
        pushTimer(() => setFlash(false), 340)
        pushTimer(() => setPop(false), 620)
      }

      const item: Fly = { id, src, route: r }
      if (reduceMotion) {
        land(item)
      } else {
        setFlying((f) => [...f, item])
      }
    },
    [flying.length, reduceMotion, land],
  )

  // callback ref: animate each flying chip exactly once, then land it
  const flyRef = useCallback(
    (node: HTMLImageElement | null, item: Fly) => {
      if (!node || flyStarted.current.has(item.id)) return
      flyStarted.current.add(item.id)

      const rig = rigRef.current
      const fork = forkRef.current
      const target =
        item.route === "vault" ? vaultBinRef.current : rollBinRef.current
      if (!rig || !fork || !target) {
        land(item)
        return
      }
      const rr = rig.getBoundingClientRect()
      const fr = fork.getBoundingClientRect()
      const tr = target.getBoundingClientRect()
      const size = 40

      const ox = fr.left + fr.width / 2 - rr.left - size / 2
      const oy = fr.top + fr.height / 2 - rr.top - size / 2
      const tx = tr.left + tr.width / 2 - rr.left - size / 2
      const ty = tr.top + tr.height * 0.6 - rr.top - size / 2
      const bow = item.route === "vault" ? 14 : -14
      const mx = (ox + tx) / 2 + bow
      const my = Math.min(oy, ty) - 4

      const anim = node.animate(
        [
          { transform: `translate(${ox}px, ${oy}px) scale(.6)`, opacity: 0 },
          {
            transform: `translate(${ox}px, ${oy}px) scale(1)`,
            opacity: 1,
            offset: 0.14,
          },
          {
            transform: `translate(${mx}px, ${my}px) scale(1)`,
            opacity: 1,
            offset: 0.55,
          },
          {
            transform: `translate(${tx}px, ${ty}px) scale(.58)`,
            opacity: 0.9,
          },
        ],
        { duration: FLY_MS, easing: "cubic-bezier(.5,.05,.4,1)", fill: "both" },
      )
      anim.onfinish = () => land(item)
    },
    [land],
  )

  // ── auto-demo once on mount: two shots straight into the vault ────────────
  useEffect(() => {
    if (reduceMotion) {
      // arrive with a small sealed vault; roll stays empty
      setVaultShots([MEMORIES[0], MEMORIES[1]])
      frameIdx.current = 2
      return
    }
    pushTimer(() => capture("vault"), 900)
    pushTimer(() => capture("vault"), 1750)
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
    },
    [],
  )

  const throwSwitch = useCallback((r: Route) => {
    setRoute(r)
    if ("vibrate" in navigator) {
      try {
        navigator.vibrate?.(8)
      } catch {}
    }
  }, [])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const rollCount = rollShots.length
  const vaultCount = vaultShots.length
  const vaultSealed = vaultCount > 0 && route === "vault"

  return (
    <div className={styles.page}>
      <button
        className={styles.close}
        onClick={() => native.postNativeEvent("close")}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M5 5l14 14M19 5L5 19"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.head}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true">
              <CameraGlyph />
            </span>
            Private Photo Vault · Private Camera
          </span>
          <h1 className={styles.title}>
            Shot straight
            <br />
            into <span className={styles.titleAccent}>the vault.</span>
          </h1>
          <p className={styles.sub}>
            An ordinary camera writes every shot to your Camera Roll. Private
            Camera captures straight into the vault — it never touches the roll.
          </p>
        </header>

        {/* ── camera console ─────────────────────────────────────────────── */}
        <div className={styles.console}>
          {/* viewfinder */}
          <div className={`${styles.viewfinder} ${pop ? styles.pop : ""}`}>
            <img
              className={styles.vfImg}
              src={currentFrame}
              alt=""
              aria-hidden="true"
            />
            <div className={styles.vfShade} />
            <div className={styles.vfGrid} />
            <span className={`${styles.corner} ${styles.tl}`} />
            <span className={`${styles.corner} ${styles.tr}`} />
            <span className={`${styles.corner} ${styles.bl}`} />
            <span className={`${styles.corner} ${styles.br}`} />
            <div className={styles.vfTop}>
              <span className={styles.liveChip}>
                <span className={styles.liveDot} />
                Private Camera · Live
              </span>
            </div>
            <span className={styles.reticle} />
            <div className={`${styles.flash} ${flash ? styles.on : ""}`} />
          </div>

          {/* routing rig */}
          <div className={styles.rig} ref={rigRef}>
            <div className={styles.forkNode} ref={forkRef} />
            <svg
              className={styles.railSvg}
              viewBox="0 0 320 62"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                className={styles.railBase}
                d="M160 6 C160 30 92 34 80 58"
              />
              <path
                className={styles.railBase}
                d="M160 6 C160 30 228 34 240 58"
              />
              <path
                className={`${styles.railLive} ${
                  route === "roll" ? styles.roll : styles.vault
                }`}
                d={
                  route === "roll"
                    ? "M160 6 C160 30 92 34 80 58"
                    : "M160 6 C160 30 228 34 240 58"
                }
              />
              <g
                className={`${styles.pivot} ${
                  route === "roll" ? styles.toRoll : styles.toVault
                }`}
              >
                <line
                  x1="160"
                  y1="4"
                  x2="160"
                  y2="26"
                  stroke={
                    route === "roll" ? "var(--lt-amber)" : "var(--lt-blue)"
                  }
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </g>
              <circle cx="160" cy="6" r="4.5" fill="#e9edf6" />
            </svg>

            {/* destination bins */}
            <div className={styles.bins}>
              <div
                className={`${styles.bin} ${styles.roll} ${
                  route === "roll" ? styles.active : ""
                }`}
                ref={rollBinRef}
                onClick={() => throwSwitch("roll")}
                role="button"
                aria-label="Route to Camera Roll (what an ordinary camera does)"
              >
                <div className={styles.binHead}>
                  <span className={styles.binIcon}>
                    <EyeGlyph />
                  </span>
                  <span className={styles.binText}>
                    <span className={styles.binTitle}>Camera Roll</span>
                    <span className={styles.binWho}>
                      anyone with your phone
                    </span>
                  </span>
                </div>
                <div className={styles.count}>
                  <span className={styles.countNum}>{rollCount}</span>
                  <span className={styles.countLbl}>
                    {rollCount === 0 ? "never written here" : "exposed"}
                  </span>
                </div>
                {rollCount === 0 ? (
                  <div className={styles.emptyRoll}>Nothing on the roll</div>
                ) : (
                  <div className={styles.stack}>
                    {rollShots.slice(-MAX_STACK).map((src, i) => (
                      <img
                        key={`${src}-${i}`}
                        className={styles.thumb}
                        src={src}
                        alt=""
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div
                className={`${styles.bin} ${styles.vault} ${
                  route === "vault" ? styles.active : ""
                }`}
                ref={vaultBinRef}
                onClick={() => throwSwitch("vault")}
                role="button"
                aria-label="Route to your Vault (Private Camera)"
              >
                {vaultSealed && (
                  <span className={styles.sealTick} aria-hidden="true">
                    <CheckGlyph />
                  </span>
                )}
                <div className={styles.binHead}>
                  <span className={styles.binIcon}>
                    <LockGlyph />
                  </span>
                  <span className={styles.binText}>
                    <span className={styles.binTitle}>Your Vault</span>
                    <span className={styles.binWho}>only you</span>
                  </span>
                </div>
                <div className={styles.count}>
                  <span className={styles.countNum}>{vaultCount}</span>
                  <span className={styles.countLbl}>
                    {vaultCount === 0 ? "sealed & empty" : "sealed"}
                  </span>
                </div>
                {vaultCount === 0 ? (
                  <div className={styles.emptyRoll}>Ready to capture</div>
                ) : (
                  <div className={styles.stack}>
                    {vaultShots.slice(-MAX_STACK).map((src, i) => (
                      <img
                        key={`${src}-${i}`}
                        className={styles.thumb}
                        src={src}
                        alt=""
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* flying capture chips */}
            {flying.map((item) => (
              <img
                key={item.id}
                ref={(n) => flyRef(n, item)}
                className={styles.fly}
                src={item.src}
                alt=""
                aria-hidden="true"
              />
            ))}
          </div>

          {/* controls: switch + shutter */}
          <div className={styles.controls}>
            <div className={styles.switch}>
              <div className={styles.switchLbl}>New photos go to</div>
              <div className={styles.seg}>
                <span
                  className={`${styles.segThumb} ${
                    route === "roll" ? styles.roll : styles.vault
                  }`}
                />
                <button
                  className={`${styles.segBtn} ${
                    route === "roll" ? styles.on : ""
                  }`}
                  onClick={() => throwSwitch("roll")}
                >
                  Camera Roll
                </button>
                <button
                  className={`${styles.segBtn} ${
                    route === "vault" ? styles.on : ""
                  }`}
                  onClick={() => throwSwitch("vault")}
                >
                  Vault
                </button>
              </div>
            </div>

            <button
              className={`${styles.shutter} ${
                route === "vault" ? styles.armedVault : styles.armedRoll
              }`}
              onClick={() => capture()}
              aria-label="Take a photo"
            >
              <span className={styles.shutterInner}>
                <CameraGlyph />
              </span>
            </button>
          </div>

          {/* live proof line */}
          <div className={styles.proof}>
            {route === "vault" ? (
              <>
                <span className={`${styles.proofStrong} ${styles.vault}`}>
                  {vaultCount} in your vault
                </span>
                <span className={styles.proofSep}>·</span>
                <span>never written to the roll</span>
              </>
            ) : (
              <>
                <span className={`${styles.proofStrong} ${styles.roll}`}>
                  Ordinary camera
                </span>
                <span className={styles.proofSep}>·</span>
                <span>every shot lands where anyone can scroll to it</span>
              </>
            )}
          </div>
        </div>

        {/* ── trust row (real safeguards) ────────────────────────────────── */}
        <div className={styles.trust}>
          {TRUST.map(({ key, Icon, name, line }) => (
            <div key={key} className={styles.trustCard}>
              <span className={styles.trustIcon}>
                <Icon />
              </span>
              <div className={styles.trustName}>{name}</div>
              <div className={styles.trustLine}>{line}</div>
            </div>
          ))}
        </div>

        {/* value line */}
        <p className={styles.valueLine}>
          Decide who can ever see a photo <b>before it exists to anyone else</b>
          . With Private Camera, the answer is only you.
        </p>

        {/* ── pricing (yearly featured) ──────────────────────────────────── */}
        <div className={styles.pricing}>
          <button
            className={styles.planYear}
            onClick={() => setPlan("yearly")}
            aria-label="Yearly plan"
          >
            <span className={styles.bestBadge}>Best value</span>
            <span className={styles.saveBadge}>Save {savePercent}%</span>
            <div className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              <span className={styles.trialPill}>
                {trialDays}-day free trial
              </span>
            </div>
            <div className={styles.priceRow}>
              <span className={styles.priceBig}>{yearlyPrice}</span>
              <span className={styles.priceUnit}>/ year</span>
            </div>
            <div className={styles.perMonth}>
              Just <b>{yearlyPerMonth}/mo</b>, billed once a year
            </div>
          </button>

          <button
            className={styles.planMonth}
            onClick={() => setPlan("monthly")}
            aria-label="Monthly plan"
          >
            <span>
              <span className={styles.planMonthL}>Monthly</span>
              <span className={styles.planMonthSub}>Billed every month</span>
            </span>
            <span className={styles.planMonthR}>{monthlyPrice}/mo</span>
          </button>
        </div>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <button className={styles.cta} onClick={purchase}>
          <CameraGlyph />
          Shoot into the vault
        </button>

        <p className={styles.terms}>
          {plan === "yearly"
            ? `${trialDays}-day free trial, then ${yearlyPrice}/year. `
            : `${monthlyPrice}/month. `}
          Auto-renews until cancelled · cancel anytime in Settings. Your photos
          are always yours — nothing is ever deleted.
        </p>
        <button
          className={styles.restore}
          onClick={() => native.postNativeEvent("already_purchased")}
        >
          Restore purchase
        </button>
      </div>
    </div>
  )
}

export default LatentPaywall
